if (!customElements.get('recently-viewed-list')) {
  class RecentlyViewedList extends HTMLElement {
    constructor() {
      super();
      this.limit = 4;
      this.sectionId = null;
      this.container = null;
      this.storageKey = 'recentlyViewed';
      this.parentContainer = null;
      this.handlePageShow = this.handlePageShow.bind(this);
      this._isConnected = false;
      this.sectionProxyId = 'recently-viewed-proxy'; // ID of the Shopify section proxy for fetching products
      this.swiperInstance = null; // Swiper instance
      this.currentHandle = null;
    }

    async connectedCallback() {
      this.limit = Number(this.dataset.limit);
      this.sectionId = this.dataset.sectionId;
      this.parentContainer = this.dataset.parentContainer;
      this.container = this.querySelector(
        `#${this.dataset.recentlyViewedContainerId}`
      );
      this._isConnected = true; // flag to track connection status
      this.swiperContainerId = this.dataset.swiperCarouselId;
      this.currentHandle = this.dataset.currentHandle;
      this.totalProductsElement = document.getElementById(
        `js-total-products-${this.sectionId}`
      );
      this.totalProductsSuffix = this.dataset.totalProductsSuffix || '';

      await this.initList();
      this.onRecentlyViewedUpdated = () => this.initList();
      this._pageShowHandler = (event) => this.handlePageShow(event);
      window.addEventListener(
        'recently-viewed-updated',
        this.onRecentlyViewedUpdated
      ); // listen for updates from the recently viewed tracker
      // fires when the page is loaded OR restored from bfcache (Back button)
      window.addEventListener('pageshow', this._pageShowHandler);
    }

    disconnectedCallback() {
      this._isConnected = false; // update flag on disconnection

      window.removeEventListener(
        'recently-viewed-updated',
        this.onRecentlyViewedUpdated
      ); // clean up the event listener

      if (this._pageShowHandler) {
        window.removeEventListener('pageshow', this._pageShowHandler); // clean up the event listener
        this._pageShowHandler = null;
      }

      this.destroySwiper(); // destroy the swiper instance
    }

    handlePageShow(event) {
      // Check if the page was loaded from the Back-Forward Cache
      if (event.persisted) {
        this.initList();
      }
    }

    async initList() {
      if (!this._isConnected) return;

      await this.updateList();
    }

    async updateList() {
      if (!this._isConnected) return; // ensure the component is still connected

      const items = this.getRecentlyViewedItems();

      // Optional: Hide the product the user is currently looking at
      const currentHandle =
        this.currentHandle || window.location.pathname.split('/').pop();
      const filteredItems = items
        .filter((h) => h !== currentHandle)
        .slice(0, this.limit);
      const sectionContainer = this.closest(this.parentContainer);
      const isEmptyHandles = filteredItems.length === 0;
      const isContainersMissing = !this.container || !sectionContainer;

      if (isContainersMissing) return;

      if (isEmptyHandles) {
        sectionContainer.style.display = 'none';
        sectionContainer.setAttribute('aria-hidden', 'true');
        sectionContainer.setAttribute('tabindex', '-1');
        return null;
      }

      // The API URL: We use the first ID in our history as the anchor
      // section_id tells Shopify which file to use for rendering the response
      // Construct the Search Query
      // Result: "handle:product-a OR handle:product-b OR handle:product-c"
      const searchQuery = filteredItems.map((h) => `handle:${h}`).join(' OR ');
      // Request the section via the Search URL + section_id
      const url = `${window.Shopify.routes.root}search?section_id=${this.sectionProxyId}&q=${searchQuery}&type=product`;

      try {
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.status}`); // Handle non-2xx HTTP responses
        }

        // Get the HTML from the response
        const html = await response.text();

        if (html && html.trim().length > 0) {
          // Create a temporary element to parse the HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          // Re-sort the fetched HTML cards to match the order in 'filtered' array
          const fragment = document.createDocumentFragment(); // Create a document fragment to hold the sorted cards

          // Loop through the filteredItems to maintain order
          filteredItems.forEach((handle) => {
            // Use the predictable data-attribute selector
            const matchingCard = tempDiv.querySelector(
              `[data-product-handle="${handle}"]`
            );
            if (matchingCard) {
              fragment.appendChild(matchingCard); // Append the matching card to the fragment
            }
          });

          // Clear skeletons and inject sorted cards
          this.container.innerHTML = '';
          this.container.appendChild(fragment);

          sectionContainer.style.display = 'block';
          sectionContainer.setAttribute('aria-hidden', 'false');
          sectionContainer.removeAttribute('tabindex');

          this.initSwiper();

          if (this.totalProductsElement && filteredItems.length > 0) {
            this.totalProductsElement.textContent = `${filteredItems.length} ${this.totalProductsSuffix}`;
          }
        } else {
          sectionContainer.style.display = 'none';
          sectionContainer.setAttribute('aria-hidden', 'true');
          sectionContainer.setAttribute('tabindex', '-1');
        }
      } catch (e) {
        console.error('Recently Viewed Error:', e);
      }
    }

    //NOTE: if you want to customize the Swiper settings, you can do another settings in the future based on the clients needs.
    initSwiper() {
      if (!this.swiperContainerId) {
        console.warn('Swiper container ID not found.');

        return null;
      }

      const navigationEnabled = {
        navigation: {
          nextEl: `#swiper-button-next-${this.sectionId}`,
          prevEl: `#swiper-button-prev-${this.sectionId}`,
        },
      };

      const mobileSlides = getSlidesConfig(
        this.dataset.slidesPerViewMobile,
        SLIDES_PER_VIEW_MOBILE_DEFAULTS
      );
      const desktopSlides = getSlidesConfig(
        this.dataset.slidesPerViewDesktop,
        SLIDES_PER_VIEW_DESKTOP_DEFAULTS
      );

      if (window.Swiper) {
        this.swiperInstance = new window.Swiper(`#${this.swiperContainerId}`, {
          speed: DEFAULT_SLIDER_SPEED, // Transition speed
          loop: false,
          watchOverflow: true, // Disable slider if slides fit within container
          preventInteractionOnTransition: true, // Prevent interactions during slide transitions
          observer: true, // Enable mutation observer
          spaceBetween: 16,
          ...mobileSlides,
          ...navigationEnabled,
          breakpoints: {
            // when window width is >= 1024px
            1024: {
              spaceBetween: 24,
              ...desktopSlides,
            },
          },
        });
      } else {
        console.warn('Swiper.js is not loaded.');
      }
    }

    destroySwiper() {
      if (this.swiperInstance) {
        try {
          if (this.swiperInstance.destroyed) {
            console.info('Swiper already destroyed');

            this.swiperInstance = null;
            return;
          }
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        } catch (error) {
          console.error('Error destroying Swiper:', error);
          this.swiperInstance = null; // Fallback
        }
      } else {
        console.log('No Swiper instance to destroy');
      }
    }

    /**
     * Safely parse and validate recently viewed items from localStorage
     * @returns {Array} Array of product handles (strings)
     */
    getRecentlyViewedItems() {
      let items = [];

      try {
        items = JSON.parse(localStorage.getItem(this.storageKey));
        if (!Array.isArray(items)) items = [];
        items = items.filter((h) => typeof h === 'string');
      } catch (e) {
        items = [];
      }

      return items;
    }
  }

  // Register the web component
  customElements.define('recently-viewed-list', RecentlyViewedList);
}
